import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { MapPin, Briefcase, ArrowRight, Bell } from 'lucide-react';
import apiClient from '@/utils/api';
import { useSectors } from '@/hooks/useSectors';
import { SectorAlertDialog } from '@/components/SectorAlertDialog';
import { Button } from '@/components/ui/button';

const SITE = 'https://trabadju.cv';

// Public SEO landing: /empregos/:sector and /empregos/:sector/:island
export default function SeoJobsPage() {
  const { sector, island } = useParams();
  const { t, i18n } = useTranslation();
  const { sectors, nameOf } = useSectors();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [alertOpen, setAlertOpen] = useState(false);
  const lang = (i18n.language || 'pt').slice(0, 2);

  useEffect(() => {
    setState('loading');
    apiClient.get('/seo/jobs', { params: { sector, island: island || undefined } })
      .then((r) => { setData(r.data); setState('ok'); })
      .catch(() => setState('error'));
  }, [sector, island]);

  if (state === 'error') {
    return <main className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="seo-not-found"><p className="text-slate-600">{t('seo.notFound')} <Link to="/jobs/search" className="text-blue-700 underline">{t('seo.allJobs')}</Link></p></main>;
  }
  const sectorName = data ? (data.sector[`name_${lang}`] || data.sector.name_pt) : '';
  const islandName = data?.island?.name;
  const title = islandName ? t('seo.titleIsland', { sector: sectorName, island: islandName }) : t('seo.titleSector', { sector: sectorName });
  const url = `${SITE}/empregos/${sector}${island ? `/${island}` : ''}`;
  const jobs = data?.jobs || [];

  return (
    <main className="min-h-screen bg-slate-50" data-testid="seo-jobs-page">
      <Helmet>
        <title>{`${title} · Trabadju.cv`}</title>
        <meta name="description" content={t('seo.metaDesc', { sector: sectorName, place: islandName || 'Cabo Verde', count: jobs.length })} />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'CollectionPage', name: title, url, about: sectorName })}</script>
      </Helmet>
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <nav className="text-xs text-slate-400 mb-4 flex flex-wrap gap-1" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white">Trabadju.cv</Link><span>/</span>
            <Link to="/jobs/search" className="hover:text-white">{t('seo.allJobs')}</Link><span>/</span>
            {island ? <Link to={`/empregos/${sector}`} className="hover:text-white">{sectorName}</Link> : <span className="text-white">{sectorName}</span>}
            {island && <><span>/</span><span className="text-white">{islandName}</span></>}
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.05]" data-testid="seo-h1">{title}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">{t('seo.intro', { sector: sectorName, place: islandName || 'Cabo Verde' })}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => setAlertOpen(true)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold" data-testid="seo-alert-btn"><Bell className="h-4 w-4 mr-2" />{t('seo.alertBtn')}</Button>
            <Link to="/candidate/register" className="inline-flex items-center h-10 px-4 rounded-md border border-white/30 text-sm font-semibold hover:bg-white/10" data-testid="seo-register-btn">{t('seo.createCv')}<ArrowRight className="h-4 w-4 ml-2" /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[1fr_300px] gap-10">
        <div>
          <h2 className="text-base md:text-lg font-bold text-slate-900 mb-4" data-testid="seo-count">{t('seo.count', { count: jobs.length })}</h2>
          {state === 'loading' && <div className="animate-pulse h-24 bg-white rounded-xl border" />}
          {state === 'ok' && jobs.length === 0 && (
            <div className="bg-white border rounded-xl p-8 text-center" data-testid="seo-empty">
              <p className="text-slate-700 font-semibold">{t('seo.emptyTitle')}</p>
              <p className="text-sm text-slate-500 mt-1">{t('seo.emptyHint')}</p>
              <Button onClick={() => setAlertOpen(true)} variant="outline" className="mt-4"><Bell className="h-4 w-4 mr-2" />{t('seo.alertBtn')}</Button>
            </div>
          )}
          <ul className="space-y-3">
            {jobs.map((j) => (
              <li key={j.id}>
                <Link to={`/jobs/${j.id}`} className="block bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-900 transition-colors" data-testid={`seo-job-${j.id}`}>
                  <p className="font-bold text-slate-900">{j[`title_${lang}`] || j.title_pt}</p>
                  <p className="text-sm text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    {j.company_name && <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{j.company_name}</span>}
                    {j.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{j.location}</span>}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <aside className="space-y-8">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{t('seo.byIsland')}</h3>
            <ul className="flex flex-wrap gap-2">
              {(data?.islands || []).map((i) => (
                <li key={i.slug}><Link to={`/empregos/${sector}/${i.slug}`} className={`inline-block text-sm px-3 py-1 rounded-full border ${island === i.slug ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:border-slate-900'}`} data-testid={`seo-island-${i.slug}`}>{i.name}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{t('seo.otherSectors')}</h3>
            <ul className="space-y-1.5">
              {sectors.filter((s) => s.slug !== sector).slice(0, 12).map((s) => (
                <li key={s.slug}><Link to={`/empregos/${s.slug}${island ? `/${island}` : ''}`} className="text-sm text-blue-700 hover:underline">{nameOf(s)}</Link></li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
      <SectorAlertDialog sector={sector} sectorLabel={sectorName} open={alertOpen} onOpenChange={setAlertOpen} />
    </main>
  );
}
